import {AbstractApi} from '@/service/api/AbstractApi'
import {QuotationRequestBean, quotationRequestSchema} from '@/service/model/QuotationRequestBean'
import {QuotationResponseBean, quotationResponseSchema} from '@/service/model/QuotationResponseBean'
import {JSONSchemaType} from 'ajv'
import {I18n} from 'vue-i18n'
import {AuthenticationService} from '@/service/authentication/AuthenticationService'
import {AlternativeQuarryBean, alternativeQuarrySchema} from '@/service/model/sitemanager/AlternativeQuarryBean'
import {SiteManagerOrderBean, siteManagerOrderSchema} from '@/service/model/sitemanager/SiteManagerOrderBean'
import {Address} from '@/utils/GeoFormatters'
import {LocationBean} from '@/service/model/LocationBean'
import {
  siteManagerQuotationRequestArraySchema,
  SiteManagerQuotationRequestBean, siteManagerQuotationRequestSchema
} from '@/service/model/sitemanager/SiteManagerQuotationRequestBean'
import {QuotationRequestV2Bean} from '@/service/model/sitemanager/QuotationRequestV2Bean'
import {QuotationRequestDetailV2Bean} from '@/service/model/sitemanager/QuotationRequestDetailV2Bean'
import {QuotationRequestV3Bean} from '@/service/model/sitemanager/QuotationRequestV3Bean'
import {
  MaterialCatalogReferenceBean,
  QuarryMaterialCatalogItemBean,
} from '@/service/model/sitemanager/QuarryMaterialCatalogBean'

export interface QuotationRequestCreationBean {
  site_id?: string
  temporary_site?: QuotationRequestTemporarySiteBean

  express_order: boolean
  delivery_type?: string // ignored for express order
  truck_type?: string // ignored for express order

  start_day?: string // ignored for express order

  material_type: string
  material_id: string
  material_quantity: number
  material_cadence?: number // ignored for express order
  material_cadence_periodicity?: number // ignored for express order

  backload_material_id?: string
  backload_material_quantity?: number

  comment?: string
}

export interface EligibleQuarryBean {
  id: string
  name: string
  company?: string | null
  phone_number?: string | null
  distance_from_site: number
  travel_duration: number
  address: {
    address_1?: string | null
    address_2?: string | null
    postal_code?: string | null
    city?: string | null
    location?: { latitude: number; longitude: number } | null
  }
  is_colas: boolean
}

export interface CustomerRequestedMaterialV2Bean {
  materialId?: string
  materialQuantity?: number
  materialType?: string
  delivered?: boolean
  truckType?: string | null
  comment?: string | null
  tgap?: boolean | null
}

export interface CustomerQuotationRequestCreationV2Bean {
  siteId: string
  address: {
    address_1?: string | null
    address_2?: string | null
    postal_code?: string | null
    city?: string | null
    location?: { latitude: number; longitude: number } | null
  }
  startDay?: string // YYYY-MM-DD
  materials: CustomerRequestedMaterialV2Bean[]
  doubleFret: boolean
}

export interface CustomerRequestedQuarryMaterialV3Bean {
  quarryMaterialId: string
  materialQuantity: number
  unit?: string | null
  materialType: string
  delivered: boolean
  truckType?: string | null
  comment?: string | null
  tgap?: boolean | null
}

export interface CustomerQuotationRequestCreationV3Bean {
  siteId: string
  address: {
    address_1?: string | null
    address_2?: string | null
    postal_code?: string | null
    city?: string | null
    location?: { latitude: number; longitude: number } | null
  }
  startDay?: string // YYYY-MM-DD
  doubleFret: boolean
  materials: CustomerRequestedQuarryMaterialV3Bean[]
}

export interface QuotationRequestForSiteBean {
  quotationRequestId: string
  reference?: string | null
  creationDate?: string | null
  startDate?: string | null
  status?: string | null
  doubleFret?: boolean | null
  batchId?: string | null
  orders?: DirectOrderBatchOrderV3Bean[] | null
}

export interface CustomerDirectOrderMaterialV3Bean {
  quarryMaterialId: string
  quantity: number
  unitId?: string | null
  comment?: string | null
}

export interface CustomerDirectOrderBatchCreationV3Bean {
  siteId: string
  startDay: string // YYYY-MM-DD
  materials: CustomerDirectOrderMaterialV3Bean[]
}

export interface DirectOrderBatchOrderV3Bean {
  orderId: string
  quarryId: string
}

export interface DirectOrderBatchV3Bean {
  batchId: string
  status: string
  orders: DirectOrderBatchOrderV3Bean[]
}

export interface QuotationResponseMaterialViewBean {
  quotationResponseMaterialId: string
  quotationRequestMaterialId: string
  materialName: string
  offeredMaterialName: string | null
  quarryMaterialName?: string | null
  quarryMaterialReference?: string | null
  materialQuantity: number
  truckType: string | null
  delivered: boolean
  simpleFreightUnitPrice: number | null
  simpleFreightPrice: number | null
  quarryId: string
  quarryName: string
  quarryDistance: number | null
}

export interface QuotationResponseViewBean {
  quotationResponseId: string
  status: string
  totalSimpleFreightPrice: number
  totalDoubleFreightPrice: number
  quotationResponseReference: string | null
  lastPriceUpdateQuarryId: string | null
  lastPriceUpdateQuarryName: string | null
  lastPriceUpdateQuarryGroup: string | null
  materials: QuotationResponseMaterialViewBean[]
  co2Balance: number | null
}

export interface CustomerQuotationResponsesViewBean {
  quotationRequestId: string
  productsCount: number
  responses: QuotationResponseViewBean[]
}

export interface MaterialByQuarryBean {
  name: string
  customName: string | null
  price: number
  maxQuantity?: number | null
}

export interface QuarrySearchRequestBean {
  address: {
    address_1?: string | null
    postal_code?: string | null
    city?: string | null
    location?: { latitude: number; longitude: number } | null
  }
  productSearchText?: string | null
  distance: number
}

export interface QuotationRequestTemporarySiteBean extends Address {
  customer_id: string

  name?: string | null

  reference?: string | null

  location?: LocationBean
}

export interface QuotationRequestAndResponsesBean {
  fee_percent: number
  spend_limit?: number | null
  request: QuotationRequestBean
  responses: Array<QuotationResponseBean>
  alternative_quarries: Array<AlternativeQuarryBean>
}

export const quotationRequestAndResponsesSchema: JSONSchemaType<QuotationRequestAndResponsesBean> = {
  type: 'object',
  properties: {
    fee_percent: {
      type: 'number'
    },
    spend_limit: {
      type: ['number'],
      nullable: true
    },
    request: quotationRequestSchema,
    responses: {
      type: 'array',
      items: quotationResponseSchema
    },
    alternative_quarries: {
      type: 'array',
      items: alternativeQuarrySchema
    }
  },
  required: ['fee_percent', 'request', 'responses', 'alternative_quarries'],
  additionalProperties: true
}

export interface QuotationRequestValidationResultBean {
  colas_internal_order_required: boolean
  order_created: boolean
  requires_validation: boolean
  order?: SiteManagerOrderBean | null
}

export interface SiteManagerValidateResponseResultBean {
  orderCreated: boolean
  requiresValidation: boolean
  colasInternalOrderRequired: boolean
}

export const siteManagerValidateResponseResultBeanSchema: JSONSchemaType<SiteManagerValidateResponseResultBean> = {
  type: 'object',
  properties: {
    orderCreated: {type: 'boolean'},
    requiresValidation: {type: 'boolean'},
    colasInternalOrderRequired: {type: 'boolean'},
  },
  required: ['orderCreated', 'requiresValidation', 'colasInternalOrderRequired'],
  additionalProperties: true,
}

export const quotationRequestValidationResultBeanSchema: JSONSchemaType<QuotationRequestValidationResultBean> = {
  type: 'object',
  properties: {
    colas_internal_order_required: {
      type: 'boolean'
    },
    order_created: {
      type: 'boolean'
    },
    requires_validation: {
      type: 'boolean'
    },
    order: {
      ...siteManagerOrderSchema,
      nullable: true
    }
  },
  required: ['order_created', 'requires_validation'],
  additionalProperties: true
}

export class QuotationRequestApi extends AbstractApi {

  constructor(i18n: I18n, authenticationService: AuthenticationService) {
    super(i18n, authenticationService)
  }

  async fetchQuotationRequests(): Promise<Array<SiteManagerQuotationRequestBean>> {
    return this.get({
      path: '/api/mp/v1/site-manager/quotation-requests',
      authentication: 'required',
      schema: siteManagerQuotationRequestArraySchema
    })
  }

  async fetchQuotationRequest(id: string): Promise<SiteManagerQuotationRequestBean> {
    return this.get({
      path: `/api/mp/v1/site-manager/quotation-requests/${id}`,
      authentication: 'required',
      schema: siteManagerQuotationRequestSchema
    })
  }

  async create(
    body: QuotationRequestCreationBean
  ): Promise<QuotationRequestAndResponsesBean> {
    return this.post<QuotationRequestAndResponsesBean>({
      path: '/api/mp/v1/site-manager/quotation-requests',
      authentication: 'required',
      body: body,
      schema: quotationRequestAndResponsesSchema
    })
  }

  async validateResponse(
    requestId: string,
    responseId: string
  ): Promise<QuotationRequestValidationResultBean> {
    return this.put<QuotationRequestValidationResultBean>({
      path: `/api/mp/v1/site-manager/quotation-requests/${requestId}/responses/${responseId}/validate`,
      authentication: 'required',
      schema: quotationRequestValidationResultBeanSchema
    })
  }

  async validateResponseV2(
    requestId: string,
    responseId: string
  ): Promise<SiteManagerValidateResponseResultBean> {
    return this.put<SiteManagerValidateResponseResultBean>({
      path: `/api/mp/v2/site-customer/quotation-requests/${requestId}/responses/${responseId}/validate`,
      authentication: 'required',
      schema: siteManagerValidateResponseResultBeanSchema,
    })
  }

  async fetchQuotationRequestsV2(): Promise<QuotationRequestV2Bean[]> {
    return this.get({
      path: '/api/mp/v2/site-customer/quotation-requests',
      authentication: 'required',
    })
  }

  async fetchQuotationRequestsV3(): Promise<QuotationRequestV3Bean[]> {
    return this.get({
      path: '/api/mp/v3/site-customer/quotation-requests',
      authentication: 'required',
    })
  }

  async fetchQuotationRequestV2(id: string): Promise<QuotationRequestDetailV2Bean> {
    return this.get({
      path: `/api/mp/v2/site-customer/quotation-requests/${id}`,
      authentication: 'required',
    })
  }

  async fetchQuotationRequestResponsesV2(id: string): Promise<CustomerQuotationResponsesViewBean> {
    return this.get({
      path: `/api/mp/v2/site-customer/quotation-requests/${id}/responses`,
      authentication: 'required',
    })
  }

  async getEligibleQuarries(bean: CustomerQuotationRequestCreationV2Bean): Promise<EligibleQuarryBean[]> {
    return this.post<EligibleQuarryBean[]>({
      path: '/api/mp/v2/site-customer/quotation-requests/eligible-quarries',
      authentication: 'required',
      body: bean,
    })
  }

  async searchQuarry(bean: QuarrySearchRequestBean): Promise<EligibleQuarryBean[]> {
    return this.post<EligibleQuarryBean[]>({
      path: '/api/mp/v2/site-customer/quotation-requests/search-quarry',
      authentication: 'required',
      body: bean,
    })
  }

  async createV2(quarryIds: string[], bean: CustomerQuotationRequestCreationV2Bean): Promise<void> {
    const formData = new FormData()
    formData.append('selectedQuarries', new Blob([JSON.stringify(quarryIds)], {type: 'application/json'}))
    formData.append('quotationRequest', new Blob([JSON.stringify(bean)], {type: 'application/json'}))
    return this.postVoid({
      path: '/api/mp/v2/site-customer/quotation-requests',
      authentication: 'required',
      rawBody: formData,
    })
  }

  async createExpressV2(
    quarryIds: string[],
    bean: CustomerQuotationRequestCreationV2Bean
  ): Promise<CustomerQuotationResponsesViewBean> {
    const formData = new FormData()
    formData.append('selectedQuarries', new Blob([JSON.stringify(quarryIds)], {type: 'application/json'}))
    formData.append('quotationRequest', new Blob([JSON.stringify(bean)], {type: 'application/json'}))
    return this.post<CustomerQuotationResponsesViewBean>({
      path: '/api/mp/v2/site-customer/quotation-requests/express',
      authentication: 'required',
      rawBody: formData,
    })
  }

  async searchQuarryMaterials(options: {
    siteId: string
    maxDistanceKm?: number
    familyId?: string
    materialTypeId?: string
    quarryIds?: string[]
  }): Promise<QuarryMaterialCatalogItemBean[]> {
    const params = new URLSearchParams()
    params.set('siteId', options.siteId)
    if (options.maxDistanceKm) {
      params.set('maxDistanceKm', String(options.maxDistanceKm))
    }
    if (options.familyId) {
      params.set('familyId', options.familyId)
    }
    if (options.materialTypeId) {
      params.set('materialTypeId', options.materialTypeId)
    }
    if (options.quarryIds?.length) {
      options.quarryIds.forEach(id => params.append('quarryIds', id))
    }
    return this.get({
      path: `/api/mp/v2/common/materials/references/quarry-materials?${params.toString()}`,
      authentication: 'required',
    })
  }

  async fetchMaterialTypes(): Promise<MaterialCatalogReferenceBean[]> {
    return this.get({
      path: '/api/mp/v2/common/materials/references/types',
      authentication: 'required',
    })
  }

  async fetchMaterialFamilies(): Promise<MaterialCatalogReferenceBean[]> {
    return this.get({
      path: '/api/mp/v2/common/materials/references/families',
      authentication: 'required',
    })
  }

  async fetchMaterialSizes(): Promise<MaterialCatalogReferenceBean[]> {
    return this.get({
      path: '/api/mp/v2/common/materials/references/sizes',
      authentication: 'required',
    })
  }

  async fetchMaterialColors(): Promise<MaterialCatalogReferenceBean[]> {
    return this.get({
      path: '/api/mp/v2/common/materials/references/colors',
      authentication: 'required',
    })
  }

  async fetchMaterialPhoto(photoId: string): Promise<Blob | null> {
    try {
      return await this.getBinary({
        path: `/api/mp/v2/common/materials/references/photos/${photoId}`,
        authentication: 'required',
        contentType: 'image',
      })
    } catch {
      return null
    }
  }

  async createV3(
    bean: CustomerQuotationRequestCreationV3Bean,
    options?: {
      forOrder?: boolean
      withDerogation?: boolean
      derogationFile?: Blob
    }
  ): Promise<QuotationRequestForSiteBean> {
    const formData = new FormData()
    formData.append('quotationRequest', new Blob([JSON.stringify(bean)], {type: 'application/json'}))
    if (options?.derogationFile) {
      formData.append('derogationFile', options.derogationFile, 'derogation-document')
    }
    const params = new URLSearchParams()
    if (options?.forOrder) {
      params.set('for_order', 'true')
    }
    if (options?.withDerogation) {
      params.set('with_derogation', 'true')
    }
    const query = params.toString()
    return this.post<QuotationRequestForSiteBean>({
      path: `/api/mp/v3/site-customer/quotation-requests/quarry-materials${query ? `?${query}` : ''}`,
      authentication: 'required',
      rawBody: formData,
    })
  }

  async createDirectOrder(bean: CustomerDirectOrderBatchCreationV3Bean): Promise<DirectOrderBatchV3Bean> {
    return this.post<DirectOrderBatchV3Bean>({
      path: '/api/mp/v3/site-customer/orders/direct',
      authentication: 'required',
      body: bean,
      extraHeaders: {'Idempotency-Key': crypto.randomUUID()},
    })
  }

  async fetchMaterialsByQuarry(quarryId: string, siteId: string): Promise<MaterialByQuarryBean[]> {
    return this.get({
      path: `/api/mp/v2/common/materials/quarries/${quarryId}/sites/${siteId}`,
      authentication: 'required',
    })
  }

  async cancelQuotationRequestV2(requestId: string): Promise<void> {
    return this.postVoid({
      path: `/api/mp/v2/site-customer/quotation-requests/${requestId}/cancel`,
      authentication: 'required',
    })
  }

  async deleteQuotationRequest(requestId: string): Promise<void> {
    return this.deleteVoid({
      path: `/api/mp/v1/site-manager/quotation-requests/${requestId}`,
      authentication: 'required',
    })
  }
}
